"use client";

import type { UseCaseGridItem } from "@/components/sections/shared/use-cases-grid";
import {
  AdVisual,
  AgentsVisual,
  AiVisual,
  BrandVisual,
  GeoVisual,
  MarketVisual,
  PriceVisual,
  ScrapingVisual,
  SeoVisual,
} from "@/components/sections/shared/use-cases-grid/card-visuals";

/* Residential use cases rendered with the shared homepage-style bento —
   the scraping card anchors the grid, each card runs its own mini-demo. */
export const RESIDENTIAL_USE_CASES: UseCaseGridItem[] = [
  {
    id: "web-scraping",
    label: "Web Scraping",
    headline: "Scrape public websites reliably at scale.",
    description:
      "Extract structured data from public websites at scale. Our proxy network of rotating residential IPs blends in with everyday traffic to improve access reliability for suitable workloads.",
    href: "/use-cases/web-scraping",
    Visual: ScrapingVisual,
    span: "anchor",
  },
  {
    id: "price-monitoring",
    label: "Price Monitoring",
    headline: "Track competitor prices in real time.",
    description:
      "Track competitor pricing across thousands of listings with accurate, location-specific results — fresh, reliable data collected at scale.",
    href: "/use-cases/price-monitoring",
    Visual: PriceVisual,
  },
  {
    id: "seo-monitoring",
    label: "SEO Monitoring",
    headline: "Track SERP rankings from any location.",
    description:
      "Check real keyword rankings from any country, city, or search engine. See what your target audience sees.",
    href: "/use-cases/seo-monitoring",
    Visual: SeoVisual,
  },
  {
    id: "ai-llm-training",
    label: "AI & LLM Training",
    headline: "Fuel your AI models with fresh web data.",
    description:
      "Collect diverse, geo-targeted web data for training and fine-tuning models — reliable access at scale, no bias from limited geography.",
    href: "/use-cases/ai-llm-training/",
    Visual: AiVisual,
  },
  {
    id: "ai-agents",
    label: "AI Agents",
    headline: "Give agents a workflow, not just an IP.",
    description:
      "An agent requests a residential IP in the target country, retrieves a localized product page, holds a sticky session for the related requests in that task, then hands the collected data back to your application. Configure country, city, and session mode via the REST API or MCP server.",
    href: "/proxy-server/residential-proxies#residential-for-developers-heading",
    Visual: AgentsVisual,
  },
  {
    id: "market-research",
    label: "Market Research",
    headline: "Understand markets from any geography.",
    description:
      "Access region-specific content, local ads, and localized pricing much as users in that market experience it.",
    href: "/use-cases/market-research/",
    Visual: MarketVisual,
  },
  {
    id: "brand-protection",
    label: "Brand Protection",
    headline: "Monitor your brand across the web.",
    description:
      "Detect counterfeit listings, unauthorized sellers, and IP violations across global marketplaces from multiple locations simultaneously.",
    href: "/use-cases/brand-protection-monitoring/",
    Visual: BrandVisual,
  },
  {
    id: "ad-verification",
    label: "Ad Verification",
    headline: "See the ads served in every market.",
    description:
      "Monitor ads, landing pages, and campaign compliance from any region — see what's being served to real users in each market.",
    href: "/use-cases/ad-verification/",
    Visual: AdVisual,
  },
  {
    id: "geolocation-testing",
    label: "Geolocation Testing",
    headline: "Test your site down to the city and ISP.",
    description:
      "Test websites, apps, pricing, and content much as users see them in each market, down to the city and ISP.",
    href: "/use-cases/localized-testing/",
    Visual: GeoVisual,
  },
];
